/**
 * Tracker Entry Page
 *
 * Full-page entry form for creating/editing a relationship tracker entry.
 * Includes sections for rating + notes, event flags, and optional feeling scales.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, parse, isValid, isFuture } from "date-fns";
import { ArrowLeft, Trash2, Loader2, Check } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { RatingSlider } from "@/components/tracker/RatingSlider";
import { OptionalScale } from "@/components/tracker/OptionalScale";
import { TipTapEditor } from "@/components/tracker/TipTapEditor";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useRelationshipEntries,
  useCreateRelationshipEntry,
  useDeleteRelationshipEntry,
} from "@/hooks/useRelationshipEntries";
import { toast } from "sonner";
import {
  EVENT_FIELDS,
  EVENT_CONFIG,
  type EventField,
} from "@/types/relationshipEntry";

type EventFlags = Record<EventField, boolean>;

const EMPTY_EVENTS: EventFlags = EVENT_FIELDS.reduce(
  (acc, field) => ({ ...acc, [field]: false }),
  {} as EventFlags
);

export function TrackerEntryPage() {
  const { date: dateParam } = useParams<{ date: string }>();
  const navigate = useNavigate();

  const [rating, setRating] = useState(5);
  const [notes, setNotes] = useState("");
  const [events, setEvents] = useState<EventFlags>(EMPTY_EVENTS);
  const [myMood, setMyMood] = useState<number | null>(null);
  const [partnerMoodGuess, setPartnerMoodGuess] = useState<number | null>(null);
  const [connectionFelt, setConnectionFelt] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Validate date param
  const parsedDate = dateParam
    ? parse(dateParam, "yyyy-MM-dd", new Date())
    : null;
  const isValidDate = parsedDate && isValid(parsedDate);
  const isFutureDate = isValidDate && isFuture(parsedDate);

  // Redirect if invalid or future date
  useEffect(() => {
    if (!isValidDate || isFutureDate) {
      navigate("/tracker", { replace: true });
    }
  }, [isValidDate, isFutureDate, navigate]);

  // Fetch existing entry for this date
  const { data: entriesData, isLoading } = useRelationshipEntries(
    dateParam || "",
    dateParam || ""
  );

  const existingEntry = entriesData?.entries?.[0] ?? null;

  const createEntry = useCreateRelationshipEntry();
  const deleteEntryMutation = useDeleteRelationshipEntry();

  // Initialize form with existing entry data
  useEffect(() => {
    if (isLoading) return;
    if (existingEntry && !initialized) {
      setRating(existingEntry.rating);
      setNotes(existingEntry.notes || "");
      setEvents(
        EVENT_FIELDS.reduce(
          (acc, field) => ({
            ...acc,
            [field]: existingEntry[field] ?? false,
          }),
          {} as EventFlags
        )
      );
      setMyMood(existingEntry.my_mood ?? null);
      setPartnerMoodGuess(existingEntry.partner_mood_guess ?? null);
      setConnectionFelt(existingEntry.connection_felt ?? null);
      setInitialized(true);
    } else if (!existingEntry && !initialized) {
      setInitialized(true);
    }
  }, [existingEntry, isLoading, initialized]);

  const toggleEvent = (field: EventField) => {
    setEvents((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    if (!dateParam) return;
    try {
      await createEntry.mutateAsync({
        entry_date: dateParam,
        rating,
        notes: notes || null,
        ...events,
        my_mood: myMood,
        partner_mood_guess: partnerMoodGuess,
        connection_felt: connectionFelt,
      });
      toast.success(existingEntry ? "Entry updated" : "Entry saved");
      navigate("/tracker");
    } catch {
      toast.error("Failed to save entry");
    }
  };

  const handleDelete = async () => {
    if (!existingEntry) return;
    try {
      await deleteEntryMutation.mutateAsync(existingEntry.id);
      toast.success("Entry deleted");
      setShowDeleteConfirm(false);
      navigate("/tracker");
    } catch {
      toast.error("Failed to delete entry");
    }
  };

  if (!isValidDate || isFutureDate) return null;

  const isEditing = !!existingEntry;
  const isSaving = createEntry.isPending;
  const isDeleting = deleteEntryMutation.isPending;
  const displayDate = format(parsedDate, "MMMM d, yyyy");

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 -ml-2 text-muted-foreground"
          onClick={() => navigate("/tracker")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Tracker
        </Button>

        {/* Date heading */}
        <h1 className="text-2xl font-bold tracking-tight mb-8">
          {displayDate}
        </h1>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-10">
            {/* Section 1: How was today */}
            <section>
              <h2 className="text-sm font-semibold text-foreground mb-3">
                How was today?
              </h2>
              <div className="space-y-4">
                <RatingSlider value={rating} onChange={setRating} />
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    Notes (optional)
                  </label>
                  <TipTapEditor
                    content={notes}
                    onChange={setNotes}
                    minHeight="200px"
                  />
                </div>
              </div>
            </section>

            {/* Section 2: Event toggles */}
            <section>
              <h2 className="text-sm font-semibold text-foreground mb-1">
                What happened?
              </h2>
              <p className="text-xs text-muted-foreground mb-3">
                Tap anything that was part of your day.
              </p>
              <div className="flex flex-wrap gap-2">
                {EVENT_FIELDS.map((field) => {
                  const config = EVENT_CONFIG[field];
                  const active = events[field];
                  return (
                    <button
                      key={field}
                      type="button"
                      onClick={() => toggleEvent(field)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                        active
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : "border-input bg-background text-foreground hover:bg-muted"
                      }`}
                      title={config.description}
                    >
                      {active && <Check className="h-3 w-3" />}
                      {config.label}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Section 3: Optional feeling scales */}
            <section>
              <h2 className="text-sm font-semibold text-foreground mb-1">
                Feelings
              </h2>
              <p className="text-xs text-muted-foreground mb-3">
                Optional — only fill in what feels relevant.
              </p>
              <div className="space-y-3">
                <OptionalScale
                  label="How am I feeling today?"
                  value={myMood}
                  onChange={setMyMood}
                />
                <OptionalScale
                  label="How do I think my partner is feeling?"
                  value={partnerMoodGuess}
                  onChange={setPartnerMoodGuess}
                />
                <OptionalScale
                  label="How connected did we feel?"
                  value={connectionFelt}
                  onChange={setConnectionFelt}
                />
              </div>
            </section>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                {isEditing && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting || isSaving}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate("/tracker")}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving || isDeleting}>
                  {isSaving && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your entry for {displayDate}. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
